import {
  CheckCircleIcon,
  CircleIcon,
  ClockIcon,
  PauseCircleIcon,
  WarningIcon,
  XCircleIcon,
} from '@phosphor-icons/react'

/**
 * Task Configuration Center
 * Consolidates all task-related constants, field definitions, mappings, and validation rules
 * This is the single source of truth for all task configurations
 */
import {
  ProjectTypeEnum,
  TaskPriorityEnum,
  TaskStatusTypeEnum,
} from '@scrambl/shared'

// === PRIORITY CONFIGURATIONS ===
export const TASK_PRIORITIES = {
  LOW: TaskPriorityEnum.LOW,
  MEDIUM: TaskPriorityEnum.MEDIUM,
  HIGH: TaskPriorityEnum.HIGH,
  URGENT: TaskPriorityEnum.URGENT,
} as const

export const PRIORITY_CONFIG = {
  [TaskPriorityEnum.LOW]: {
    label: 'Low',
    color: 'bg-gray-100 text-gray-800',
    dotColor: 'bg-gray-400',
    barColor: 'bg-slate-500 dark:bg-slate-400',
    bars: 1,
    order: 1,
    isUrgent: false,
  },
  [TaskPriorityEnum.MEDIUM]: {
    label: 'Medium',
    color: 'bg-blue-100 text-blue-800',
    dotColor: 'bg-blue-400',
    barColor: 'bg-blue-500',
    bars: 2,
    order: 2,
    isUrgent: false,
  },
  [TaskPriorityEnum.HIGH]: {
    label: 'High',
    color: 'bg-orange-100 text-orange-800',
    dotColor: 'bg-orange-400',
    barColor: 'bg-orange-500',
    bars: 3,
    order: 3,
    isUrgent: false,
  },
  [TaskPriorityEnum.URGENT]: {
    label: 'Urgent',
    color: 'bg-red-100 text-red-800',
    dotColor: 'bg-red-400',
    barColor: 'text-red-500',
    bars: 0,
    order: 4,
    isUrgent: true,
    icon: WarningIcon,
  },
} as const

export const PRIORITY_OPTIONS = [
  { label: 'Low', value: TaskPriorityEnum.LOW },
  { label: 'Medium', value: TaskPriorityEnum.MEDIUM },
  { label: 'High', value: TaskPriorityEnum.HIGH },
  { label: 'Urgent', value: TaskPriorityEnum.URGENT },
] as const

// === STATUS CONFIGURATIONS ===
export const STATUS_TYPE_CONFIG = {
  [TaskStatusTypeEnum.NOT_STARTED]: {
    label: 'Not Started',
    color: 'bg-gray-100 text-gray-800',
    colorDetailed:
      'bg-gray-600 text-white hover:bg-gray-700 dark:bg-gray-500 dark:hover:bg-gray-400',
    hexColor: '#94a3b8',
    category: 'todo',
    icon: CircleIcon,
    order: 1,
  },
  [TaskStatusTypeEnum.IN_PROGRESS]: {
    label: 'In Progress',
    color: 'bg-blue-100 text-blue-800',
    colorDetailed:
      'bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-400',
    hexColor: '#3b82f6',
    category: 'in-progress',
    icon: ClockIcon,
    order: 2,
  },
  [TaskStatusTypeEnum.COMPLETED]: {
    label: 'Completed',
    color: 'bg-green-100 text-green-800',
    colorDetailed:
      'bg-green-600 text-white hover:bg-green-700 dark:bg-green-500 dark:hover:bg-green-400',
    hexColor: '#22c55e',
    category: 'done',
    icon: CheckCircleIcon,
    order: 3,
  },
  [TaskStatusTypeEnum.ON_HOLD]: {
    label: 'On Hold',
    color: 'bg-yellow-100 text-yellow-800',
    colorDetailed:
      'bg-orange-600 text-white hover:bg-orange-700 dark:bg-orange-500 dark:hover:bg-orange-400',
    hexColor: '#f59e0b',
    category: 'blocked',
    icon: PauseCircleIcon,
    order: 4,
  },
  [TaskStatusTypeEnum.CANCELED]: {
    label: 'Canceled',
    color: 'bg-red-100 text-red-800',
    colorDetailed:
      'bg-red-600 text-white hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-400',
    hexColor: '#ef4444',
    category: 'canceled',
    icon: XCircleIcon,
    order: 5,
  },
} as const

// Default Status Options (fallback when no custom statuses available)
export const DEFAULT_STATUS_OPTIONS = [
  {
    label: 'To Do',
    value: 'todo',
    color: STATUS_TYPE_CONFIG.NOT_STARTED.hexColor,
    id: 'todo',
    statusTypeId: TaskStatusTypeEnum.NOT_STARTED,
  },
  {
    label: 'In Progress',
    value: 'in-progress',
    color: STATUS_TYPE_CONFIG.IN_PROGRESS.hexColor,
    id: 'in-progress',
    statusTypeId: TaskStatusTypeEnum.IN_PROGRESS,
  },
  {
    label: 'Done',
    value: 'done',
    color: STATUS_TYPE_CONFIG.COMPLETED.hexColor,
    id: 'done',
    statusTypeId: TaskStatusTypeEnum.COMPLETED,
  },
  {
    label: 'On Hold',
    value: 'on-hold',
    color: STATUS_TYPE_CONFIG.ON_HOLD.hexColor,
    id: 'on-hold',
    statusTypeId: TaskStatusTypeEnum.ON_HOLD,
  },
  {
    label: 'Canceled',
    value: 'canceled',
    color: STATUS_TYPE_CONFIG.CANCELED.hexColor,
    id: 'canceled',
    statusTypeId: TaskStatusTypeEnum.CANCELED,
  },
] as const

// === FIELD CONFIGURATIONS ===
export const TASK_FIELD_CONFIG = {
  status: {
    label: 'Status',
    type: 'select',
    required: true,
    sortable: true,
    filterable: true,
    width: 'w-36',
    alignment: 'justify-start',
    responsive: true,
  },
  priority: {
    label: 'Priority',
    type: 'select',
    required: false,
    sortable: true,
    filterable: true,
    width: 'w-12',
    alignment: 'justify-center',
    responsive: 'hidden sm:flex',
  },
  assignees: {
    label: 'Assignees',
    type: 'multi-select',
    required: false,
    sortable: true,
    filterable: true,
    width: 'w-40',
    alignment: 'justify-start',
    responsive: 'hidden md:flex',
  },
  dueDate: {
    label: 'Due Date',
    type: 'date',
    required: false,
    sortable: true,
    filterable: true,
    width: 'w-44',
    alignment: 'justify-start',
    responsive: 'hidden lg:flex',
  },
  storyPoints: {
    label: 'Story Points',
    type: 'number',
    required: false,
    sortable: true,
    filterable: true,
    width: 'w-16',
    alignment: 'justify-center',
    responsive: 'hidden xl:flex',
  },
  group: {
    label: 'Group',
    type: 'select',
    required: false,
    sortable: true,
    filterable: true,
    width: 'w-32',
    alignment: 'justify-start',
    responsive: 'hidden md:flex',
  },
  dependencies: {
    label: 'Dependencies',
    type: 'indicator',
    required: false,
    sortable: false,
    filterable: false,
    width: 'w-8',
    alignment: 'justify-center',
    responsive: 'hidden sm:flex',
  },
  comments: {
    label: 'Comments',
    type: 'indicator',
    required: false,
    sortable: false,
    filterable: false,
    width: 'w-20',
    alignment: 'justify-center',
    responsive: 'hidden sm:flex',
  },
  subtasks: {
    label: 'Subtasks',
    type: 'indicator',
    required: false,
    sortable: false,
    filterable: false,
    width: 'w-12',
    alignment: 'justify-center',
    responsive: true,
  },
  name: {
    label: 'Name',
    type: 'text',
    required: true,
    sortable: true,
    filterable: true,
    width: 'flex-1 min-w-0',
    alignment: 'justify-start',
    responsive: true,
  },
  taskNumber: {
    label: 'Task #',
    type: 'text',
    required: false,
    sortable: true,
    filterable: true,
    width: 'w-20',
    alignment: 'justify-start',
    responsive: 'hidden sm:flex',
  },
  description: {
    label: 'Description',
    type: 'textarea',
    required: false,
    sortable: false,
    filterable: true,
    width: 'flex-1',
    alignment: 'justify-start',
    responsive: true,
  },
} as const

// === LAYOUT CONFIGURATIONS ===
export const TASK_VARIANT_STYLES = {
  list: 'group relative flex items-start w-full py-1 border-b border-border/50 hover:bg-accent/10 transition-colors duration-200',
  card: 'group relative p-3 bg-card border border-border rounded-card shadow-sm hover:shadow-md transition-all duration-200',
  table:
    'group relative flex items-start w-full py-1 border-b border-border/50 hover:bg-accent/10 transition-colors duration-200',
  compact:
    'group relative flex items-start w-full px-2 py-0.5 hover:bg-accent/10 transition-colors duration-200',
} as const

export const LAYOUT_CONFIG = {
  minHeight: {
    list: 'min-h-[1.5rem]',
    card: 'min-h-[3rem]',
    table: 'min-h-[1.5rem]',
    compact: 'min-h-[1.25rem]',
  },
  padding: {
    list: '',
    card: 'px-2',
    table: '',
    compact: '',
  },
  gaps: {
    list: 'gap-1.5',
    card: 'gap-1.5',
    table: 'gap-1.5',
    compact: 'gap-1',
  },
} as const

// === FIELD SETS FOR DIFFERENT VARIANTS ===
export const DEFAULT_FIELD_SETS = {
  list: [
    'status',
    'priority',
    'assignees',
    'dueDate',
    'subtasks',
    'comments',
    'dependencies',
  ] as const,
  card: [
    'assignees',
    'dueDate',
    'priority',
    'subtasks',
    'comments',
    'dependencies',
  ] as const,
  table: [
    'status',
    'priority',
    'assignees',
    'dueDate',
    'storyPoints',
    'group',
    'subtasks',
    'comments',
    'dependencies',
  ] as const,
  compact: [
    'status',
    'priority',
    'subtasks',
    'comments',
    'dependencies',
  ] as const,
} as const

// === VALIDATION RULES ===
interface ValidationRuleConfig {
  required?: boolean
  minLength?: number
  maxLength?: number
  pattern?: RegExp
  message: string
  min?: number
  max?: number
  integer?: boolean
  allowedValues?: readonly string[]
  maxCount?: number
}

export const VALIDATION_RULES: Record<string, ValidationRuleConfig> = {
  taskName: {
    required: true,
    minLength: 1,
    maxLength: 255,
    pattern: /^.+$/,
    message: 'Task name is required and must be between 1-255 characters',
  },
  description: {
    maxLength: 25000,
    message: 'Description must be less than 25000 characters',
  },
  storyPoints: {
    min: 0,
    max: 100,
    integer: true,
    message: 'Story points must be a whole number between 0-100',
  },
  dueDate: {
    message: 'Due date must be a valid date',
  },
  priority: {
    required: false,
    allowedValues: Object.values(TaskPriorityEnum),
    message: 'Priority must be one of: Low, Medium, High, Urgent',
  },
  status: {
    required: true,
    message: 'Status is required',
  },
  assignees: {
    maxCount: 10,
    message: 'Cannot assign more than 10 people to a task',
  },
  taskNumber: {
    pattern: /^[A-Z]{2,10}-\d{1,6}$/,
    message: 'Task number must be in format: PREFIX-NUMBER (e.g., TASK-100)',
  },
} as const

// === SORTING CONFIGURATIONS ===
export const SORT_OPTIONS = [
  { value: 'dueDate', label: 'Due Date' },
  { value: 'priority', label: 'Priority' },
  { value: 'name', label: 'Name' },
  { value: 'createdAt', label: 'Date Created' },
  { value: 'updatedAt', label: 'Last Updated' },
  { value: 'status', label: 'Status' },
  { value: 'assignees', label: 'Assignees' },
  { value: 'manual', label: 'Manual Order' },
] as const

// === DEFAULT VALUES ===
export const DEFAULT_VALUES = {
  priority: TaskPriorityEnum.MEDIUM,
  status: 'todo',
  storyPoints: null,
  dueDate: null,
  assignees: [],
  description: '',
  tags: [],
  customFields: {},
} as const

// === UTILITY FUNCTIONS ===

/**
 * Get priority configuration for a given priority value
 */
export function getPriorityConfig(priority: string) {
  return (
    PRIORITY_CONFIG[priority as keyof typeof PRIORITY_CONFIG] ||
    PRIORITY_CONFIG[TaskPriorityEnum.MEDIUM]
  )
}

/**
 * Get status type configuration for a given status type
 */
export function getStatusTypeConfig(statusType: string) {
  return (
    STATUS_TYPE_CONFIG[statusType as keyof typeof STATUS_TYPE_CONFIG] ||
    STATUS_TYPE_CONFIG[TaskStatusTypeEnum.NOT_STARTED]
  )
}

/**
 * Get field configuration for a given field type
 */
export function getFieldConfig(fieldType: string) {
  return TASK_FIELD_CONFIG[fieldType as keyof typeof TASK_FIELD_CONFIG]
}

/**
 * Get field sets based on variant and project type
 */
export function getFieldsForVariant(
  variant: keyof typeof DEFAULT_FIELD_SETS,
  projectType?: string,
): string[] {
  const baseFields = [...DEFAULT_FIELD_SETS[variant]]

  // For SCRUM projects, prioritize group (Epic) and storyPoints
  if (projectType === ProjectTypeEnum.SCRUM) {
    switch (variant) {
      case 'list':
        return [
          'status',
          'group',
          'assignees',
          'dueDate',
          'storyPoints',
          'subtasks',
          'comments',
          'dependencies',
        ]
      case 'table':
        return [
          'status',
          'group',
          'assignees',
          'dueDate',
          'storyPoints',
          'subtasks',
          'comments',
          'dependencies',
        ]
      case 'card':
        return [
          'assignees',
          'group',
          'status',
          'dueDate',
          'subtasks',
          'comments',
          'dependencies',
        ]
      case 'compact':
        return ['status', 'group', 'subtasks', 'comments', 'dependencies']
      default:
        return baseFields
    }
  }

  return baseFields
}

/**
 * Get column widths for responsive layout
 */
export function getColumnWidths(isSubtask = false, projectType?: string) {
  const baseWidths = {
    checkbox: 'w-4',
    dragHandle: 'w-3',
    expandArrow: 'w-6',
    taskName: 'flex-1 min-w-0',
    indicators: 'w-12',
    subtasks: 'w-12',
    status: 'w-36',
    priority: 'w-12 hidden sm:flex',
    assignees: 'w-40 hidden md:flex',
    dueDate: 'w-44 hidden lg:flex',
    storyPoints: 'w-16 hidden xl:flex',
    dependencies: 'w-8 hidden sm:flex',
    comments: 'w-20 hidden sm:flex',
    group: 'w-32 hidden md:flex',
    actions: 'w-8',
  }

  if (isSubtask) {
    return {
      ...baseWidths,
      checkbox: 'w-6',
      expandArrow: 'w-4',
      subtasks: 'w-8',
      status: 'w-28',
      priority: 'w-8 hidden sm:flex',
      assignees: 'w-12 hidden md:flex',
      dueDate: 'w-32 hidden lg:flex',
      storyPoints: 'w-12 hidden xl:flex',
      group: 'w-24 hidden md:flex',
      dependencies: 'w-6 hidden sm:flex',
      comments: 'w-20 hidden sm:flex',
    }
  }

  if (projectType === ProjectTypeEnum.SCRUM) {
    return {
      ...baseWidths,
      assignees: 'w-16 hidden md:flex',
      group: 'w-36 hidden md:flex',
      storyPoints: 'w-8 hidden sm:flex',
    }
  }

  return baseWidths
}

/**
 * Get column alignment configuration
 */
export function getColumnAlignment(centered = false) {
  const baseAlignment = {
    checkbox: 'flex items-center justify-center',
    dragHandle: 'flex items-center justify-center',
    expandArrow: 'flex items-center justify-center',
    taskName: 'flex items-center',
    indicators: 'flex items-center justify-start',
    subtasks: 'flex items-center justify-center',
    status: 'flex items-center justify-start',
    priority: 'flex items-center justify-center',
    assignees: 'flex items-center justify-start',
    dueDate: 'flex items-center justify-start',
    storyPoints: 'flex items-center justify-center',
    dependencies: 'flex items-center justify-center',
    comments: 'flex items-center justify-center',
    group: 'flex items-center justify-start',
    actions: 'flex items-center justify-center',
  }

  // For subtasks, use top alignment
  if (!centered) {
    return Object.fromEntries(
      Object.entries(baseAlignment).map(([key, value]) => [
        key,
        value
          .replace('items-center', 'items-start')
          .replace('justify-center', 'justify-center') + ' pt-0',
      ]),
    )
  }

  return baseAlignment
}

/**
 * Validate a field value against its validation rules
 */
export function validateField(
  fieldType: string,
  value: unknown,
): string | null {
  const rules = VALIDATION_RULES[fieldType]
  if (!rules) return null

  switch (fieldType) {
    case 'taskName':
    case 'name':
      if (!value || typeof value !== 'string') {
        return rules.message
      }
      if (
        value.length < (rules.minLength || 0) ||
        value.length > (rules.maxLength || 255)
      ) {
        return rules.message
      }
      break

    case 'description':
      if (
        value &&
        typeof value === 'string' &&
        value.length > (rules.maxLength || 5000)
      ) {
        return rules.message
      }
      break

    case 'storyPoints':
      if (value !== null && value !== undefined) {
        const num = Number(value)
        if (
          isNaN(num) ||
          num < (rules.min || 0) ||
          num > (rules.max || 100) ||
          !Number.isInteger(num)
        ) {
          return rules.message
        }
      }
      break

    case 'dueDate':
      if (value && (!(value instanceof Date) || isNaN(value.getTime()))) {
        return rules.message
      }
      break

    case 'priority':
      if (value && !rules.allowedValues?.includes(value as string)) {
        return rules.message
      }
      break

    case 'assignees':
      if (
        value &&
        Array.isArray(value) &&
        value.length > (rules.maxCount || 10)
      ) {
        return rules.message
      }
      break

    case 'taskNumber':
      if (value && typeof value === 'string' && !rules.pattern?.test(value)) {
        return rules.message
      }
      break
  }

  return null
}

/**
 * Get common UI classes for consistency
 */
export function getCommonClasses() {
  return {
    // Minimal padding for compact table rows
    editableCellDisplay:
      'editable-cell-display relative group flex items-center w-full h-full overflow-hidden',
    hoverTransition: 'hover:bg-accent transition-colors duration-200',
    loadingSpinner: 'animate-spin',
    badge:
      'h-6 px-1.5 rounded-md flex items-center w-full justify-start text-xs',
    popoverTrigger:
      'flex items-center gap-1.5 w-full cursor-pointer rounded hover:bg-accent transition-colors duration-200',
  }
}

/**
 * Check if a date is overdue
 */
export function isOverdue(dueDate?: string | Date | null): boolean {
  if (!dueDate) return false
  const due = new Date(dueDate)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return due < today
}

/**
 * Check if a date is due today
 */
export function isDueToday(dueDate?: string | Date | null): boolean {
  if (!dueDate) return false
  const due = new Date(dueDate)
  const today = new Date()
  return (
    due.getDate() === today.getDate() &&
    due.getMonth() === today.getMonth() &&
    due.getFullYear() === today.getFullYear()
  )
}

/**
 * Get task item styles based on variant and state
 */
export function getTaskItemStyles(
  variant: keyof typeof TASK_VARIANT_STYLES,
  options: {
    selected?: boolean
    dragging?: boolean
    expanded?: boolean
    depth?: number
  } = {},
) {
  const { selected, dragging, expanded, depth = 0 } = options

  const baseStyles = TASK_VARIANT_STYLES[variant]
  const layoutClasses = {
    minHeight: LAYOUT_CONFIG.minHeight[variant],
    padding: LAYOUT_CONFIG.padding[variant],
    gap: LAYOUT_CONFIG.gaps[variant],
  }

  let classes = `${baseStyles} ${layoutClasses.minHeight} ${layoutClasses.padding} ${layoutClasses.gap}`

  if (selected) classes += ' bg-accent/20 border-primary/20'
  if (dragging) classes += ' opacity-50 z-50'
  if (expanded) classes += ' border-l-4 border-l-primary/30'
  if (depth > 0) classes += ` ml-${Math.min(depth * 4, 16)}`

  return classes
}

/**
 * Create a task comparator function for sorting
 */
export function createTaskComparator(
  field: string,
  direction: 'asc' | 'desc' = 'asc',
) {
  return (a: Record<string, unknown>, b: Record<string, unknown>) => {
    const aValue = a[field]
    const bValue = b[field]

    // Handle null/undefined values
    if (aValue == null && bValue == null) return 0
    if (aValue == null) return direction === 'asc' ? 1 : -1
    if (bValue == null) return direction === 'asc' ? -1 : 1

    // Compare values
    if (aValue < bValue) return direction === 'asc' ? -1 : 1
    if (aValue > bValue) return direction === 'asc' ? 1 : -1
    return 0
  }
}
